import React, {FC} from "react";
import {Alert, Checkbox, DatePicker, Form, InputNumber, Select, Typography} from "antd";
import {PaymentMethodField} from "./ParticipationFields";
import {buildRoomAvailabilityOptionKey} from "./publicRegistrationModel";
import {useRegistrationCopy} from "./registrationCopy";
import type {PaymentMethodValue, PublicRoomAvailability} from "./types";

// Optional room reservation; the room is held on submit, before payment.
export const AccommodationSection: FC<{
    rooms: PublicRoomAvailability[];
    reserveAccommodation: boolean;
    selectedRoom?: PublicRoomAvailability;
    estimate: number;
    paymentMethods: PaymentMethodValue[];
}> = ({rooms, reserveAccommodation, selectedRoom, estimate, paymentMethods}) => {
    const {labels, formatAmount} = useRegistrationCopy();
    const requiredRule = [{required: true, message: labels.required}];

    return (
        <section className="public-registration-form-section">
            <Typography.Title level={2}>{labels.accommodationSection}</Typography.Title>
            <Typography.Paragraph>{labels.accommodationHelp}</Typography.Paragraph>
            <Form.Item name="reserveAccommodation" valuePropName="checked">
                <Checkbox>{labels.reserveAccommodation}</Checkbox>
            </Form.Item>

            {reserveAccommodation ? (
                <>
                    <Alert
                        type="info"
                        showIcon
                        message={labels.accommodationReservationNotice}
                        description={labels.accommodationOfferNotice}
                    />

                    <Form.Item name="accommodationSelectionKey" label={labels.accommodationChoice} rules={requiredRule}>
                        <Select
                            options={rooms.map(room => {
                                const remaining = Math.min(...room.nights.map(night => night.remainingRooms));
                                return {
                                    value: buildRoomAvailabilityOptionKey(room),
                                    label: `${room.hotelName} - ${room.roomTypeName} (${formatAmount(room.pricePerNight)} / ${labels.roomPerNight}, ${remaining} ${labels.roomsAvailable})`
                                };
                            })}
                        />
                    </Form.Item>

                    <div className="public-registration-form-grid public-registration-form-grid--compact">
                        <Form.Item name="accommodationArrivalDate" label={labels.arrivalDate} rules={requiredRule}>
                            <DatePicker style={{width: "100%"}}/>
                        </Form.Item>
                        <Form.Item name="accommodationDepartureDate" label={labels.departureDate} rules={requiredRule}>
                            <DatePicker style={{width: "100%"}}/>
                        </Form.Item>
                        <Form.Item name="accommodationRoomCount" label={labels.roomCount} rules={requiredRule}>
                            <InputNumber min={1} step={1} style={{width: "100%"}}/>
                        </Form.Item>
                    </div>

                    {selectedRoom ? (
                        <div className="public-registration-estimate">
                            <Typography.Text>{labels.accommodationEstimate}</Typography.Text>
                            <Typography.Title level={3}>
                                {formatAmount(estimate)}
                            </Typography.Title>
                        </div>
                    ) : null}

                    <PaymentMethodField name="accommodationPaymentMethod" methods={paymentMethods}/>
                </>
            ) : null}
        </section>
    );
};
